import { useId, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApprovalType, GlobalApprovalMode, ReviewerApproverMode } from '@mg/shared';
import type { Tenant, UpdateTenantDto } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useTenant } from '../../auth/TenantContext';
import { useIsWorkspaceAdmin } from '../../auth/useWorkspaceAdmin';
import { Card, CardBody, CardHeader } from '../ui/Card';
import { Button } from '../ui/Button';
import { FormField, Select } from '../ui/Field';
import { useFooterSave } from '../settings/footerSave';
import { ApproverAssignmentsCard } from '../../features/approval/ApproverAssignmentsCard';

const GLOBAL_MODE_LABEL: Record<GlobalApprovalMode, string> = {
  [GlobalApprovalMode.CAMPAIGN]: 'Campaign level',
  [GlobalApprovalMode.POST]: 'Per-Post',
  [GlobalApprovalMode.NONE]: 'No approvals',
  [GlobalApprovalMode.PER_WORKSPACE]: 'Set by each Brand Workspace',
};
const REVIEWER_MODE_LABEL: Record<ReviewerApproverMode, string> = {
  [ReviewerApproverMode.BOTH]: 'Reviewer then Approver',
  [ReviewerApproverMode.APPROVER_ONLY]: 'Approver only',
  [ReviewerApproverMode.PER_WORKSPACE]: 'Set by each Brand Workspace',
};

/**
 * The workspace's Approvals section: "Workspace approval" + "Reviewer flow" (with their OWN Save
 * button, separate from the main workspace form) followed by the "Approval Role Assignment" matrix.
 * The two dropdowns' LIVE values are passed to the matrix so changing them updates the matrix's
 * freeze/unlock immediately (e.g. picking "Reviewer then Approver" unlocks the R checkboxes right
 * away, before Save). Each selector only applies (and only persists) when the install-wide policy
 * defers to the workspace (PER_WORKSPACE); otherwise it's greyed and shows the effective value.
 *
 * On the Brand Workspace v2 Workflow page (inside a FooterSaveProvider) the section hands its save to
 * the page's sticky Save footer, first of the page's three (`order` 10, then the image limit, then the
 * role matrix: E39), and hides its own Save button. Everywhere else the registration does nothing and
 * the section is exactly as it was. The optional props are the v2 page's: who may edit (by the
 * workspace being edited, E20) and the ids of the two cards, the side menu's sections.
 */
export function WorkspaceApprovalsSection({
  workspace,
  canEdit: canEditProp,
  approvalsId,
  rolesId,
}: {
  workspace: Tenant;
  /** Who may change these settings. Default: today's rule, useIsWorkspaceAdmin. */
  canEdit?: boolean;
  /** The Approvals card's id: a side-menu section, and the test-mode link's anchor. */
  approvalsId?: string;
  /** The Approval Role Assignment card's id: a side-menu section. */
  rolesId?: string;
}) {
  const { refresh } = useTenant();
  const isWorkspaceAdmin = useIsWorkspaceAdmin();
  const canEdit = canEditProp ?? isWorkspaceAdmin;
  const baseId = useId();
  const approvalId = `${baseId}-approval`;
  const reviewerId = `${baseId}-reviewer`;
  const autoPublishId = `${baseId}-autopublish`;

  const platformQuery = useQuery({
    queryKey: ['platform-settings'],
    queryFn: ({ signal }) => api.getPlatformSettings(signal),
  });
  const globalMode = platformQuery.data?.globalApprovalMode;
  const perWorkspaceActive = globalMode === GlobalApprovalMode.PER_WORKSPACE;
  const globalReviewerMode = platformQuery.data?.reviewerApproverMode;
  const perWorkspaceReviewerActive = globalReviewerMode === ReviewerApproverMode.PER_WORKSPACE;

  const [approvalType, setApprovalType] = useState<ApprovalType>(workspace.approvalType);
  const [reviewerMode, setReviewerMode] = useState<ReviewerApproverMode>(
    workspace.reviewerApproverMode,
  );
  const [autoPublish, setAutoPublish] = useState<boolean>(workspace.autoPublishEnabled);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The value each dropdown SHOWS: the workspace's own when it applies, else the install-wide value.
  const effectiveApprovalMode: ApprovalType | GlobalApprovalMode = perWorkspaceActive
    ? approvalType
    : (globalMode ?? approvalType);
  const effectiveReviewerMode: ReviewerApproverMode = perWorkspaceReviewerActive
    ? reviewerMode
    : (globalReviewerMode ?? reviewerMode);

  // Only editable selectors contribute to dirtiness (an overridden one is greyed).
  const dirty =
    (perWorkspaceActive && approvalType !== workspace.approvalType) ||
    (perWorkspaceReviewerActive && reviewerMode !== workspace.reviewerApproverMode) ||
    autoPublish !== workspace.autoPublishEnabled;

  const save = useMutation({
    mutationFn: () => {
      const dto: UpdateTenantDto = {
        // Never overwrite the stored value with an override — only persist what this workspace owns.
        ...(perWorkspaceActive ? { approvalType } : {}),
        ...(perWorkspaceReviewerActive ? { reviewerApproverMode: reviewerMode } : {}),
        // Auto-publishing is always a per-workspace setting (no install-wide override).
        autoPublishEnabled: autoPublish,
      };
      return api.updateTenant(workspace.id, dto);
    },
    onSuccess: async () => {
      setError(null);
      setSaved(true);
      await refresh();
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not save the approval settings.'),
  });

  const { inFooter } = useFooterSave({
    id: 'approvals',
    order: 10,
    dirty,
    canSave: canEdit,
    saving: save.isPending,
    // The mutation's own onError shows why a save failed; the footer only needs the outcome.
    save: () => save.mutateAsync().then(
      () => true,
      () => false,
    ),
    discard: () => {
      setApprovalType(workspace.approvalType);
      setReviewerMode(workspace.reviewerApproverMode);
      setAutoPublish(workspace.autoPublishEnabled);
      setSaved(false);
      setError(null);
    },
  });

  return (
    <div className="space-y-4">
      <Card
        id={approvalsId}
        tabIndex={approvalsId ? -1 : undefined}
        className={approvalsId ? 'scroll-mt-6' : undefined}
      >
        <CardHeader
          title="Approvals"
          description="How content in this workspace is reviewed before it can be scheduled or published, and who signs off."
        />
        <CardBody className="space-y-4">
          <FormField
            label="Workspace approval"
            htmlFor={approvalId}
            hint={
              !platformQuery.data
                ? 'Loading approval policy…'
                : perWorkspaceActive
                  ? 'How content in this workspace is reviewed before it can be scheduled or published.'
                  : `Overridden by the install-wide setting: ${GLOBAL_MODE_LABEL[globalMode!]}.`
            }
          >
            <Select
              id={approvalId}
              value={effectiveApprovalMode}
              disabled={!canEdit || save.isPending || !perWorkspaceActive}
              onChange={(e) => {
                setSaved(false);
                setApprovalType(e.target.value as ApprovalType);
              }}
            >
              <option value={ApprovalType.CAMPAIGN}>Campaign level</option>
              <option value={ApprovalType.POST}>Per-Post</option>
              <option value={ApprovalType.NONE}>No approvals</option>
            </Select>
          </FormField>

          <FormField
            label="Reviewer flow"
            htmlFor={reviewerId}
            hint={
              !platformQuery.data
                ? 'Loading approval policy…'
                : perWorkspaceReviewerActive
                  ? 'Whether this workspace uses a two-stage Reviewer → Approver flow, or Approver only.'
                  : `Overridden by the install-wide setting: ${REVIEWER_MODE_LABEL[globalReviewerMode!]}.`
            }
          >
            <Select
              id={reviewerId}
              value={effectiveReviewerMode}
              disabled={!canEdit || save.isPending || !perWorkspaceReviewerActive}
              onChange={(e) => {
                setSaved(false);
                setReviewerMode(e.target.value as ReviewerApproverMode);
              }}
            >
              <option value={ReviewerApproverMode.BOTH}>Reviewer then Approver</option>
              <option value={ReviewerApproverMode.APPROVER_ONLY}>Approver only</option>
            </Select>
          </FormField>

          <FormField
            label="Auto-publishing"
            htmlFor={autoPublishId}
            hint="When on, a post with a scheduled time is published to its connected account automatically once that time arrives. Off by default — nothing posts to a live account on a timer until you turn this on."
          >
            <label htmlFor={autoPublishId} className="flex items-center gap-2.5 text-sm text-ink-strong">
              <input
                id={autoPublishId}
                type="checkbox"
                checked={autoPublish}
                disabled={!canEdit || save.isPending}
                onChange={(e) => {
                  setSaved(false);
                  setAutoPublish(e.target.checked);
                }}
                className="focus-ring h-4 w-4 rounded border-line-input text-action"
              />
              Automatically publish scheduled posts at their scheduled time
            </label>
          </FormField>

          {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
          {saved && !dirty && <p className="text-sm text-success dark:text-success-dark">Saved.</p>}

          {canEdit && !inFooter && (
            <div className="flex justify-end">
              <Button loading={save.isPending} disabled={!dirty} onClick={() => save.mutate()}>
                Save approvals
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <ApproverAssignmentsCard
        approvalTypeOverride={approvalType}
        reviewerModeOverride={reviewerMode}
        id={rolesId}
        canEdit={canEditProp}
      />
    </div>
  );
}
